import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, Badge, SectionTitle } from "../primitives";

export const SharePointSettings = () => {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [drives, setDrives] = useState<any[]>([]);
  const [loadingDrives, setLoadingDrives] = useState(false);
  const [msg, setMsg] = useState("");
  // SHAREPOINT_HOST is a configStore-managed key (PR #37), edited via the generic
  // config-keys API — kept fully separate from the sharepoint_* app_settings the
  // site/library picker below uses. Like all config-keys, GET returns source only
  // (never the stored value), so the input is write-only with a source pill.
  const [host, setHost] = useState("");
  const [hostStatus, setHostStatus] = useState<{ source: string; hasDbValue: boolean } | null>(null);
  const [hostSaving, setHostSaving] = useState(false);
  const [hostMsg, setHostMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    apiCall("/settings").then(d => { setSettings(d.settings || {}); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadHostStatus = () => {
    apiCall("/settings/config-keys")
      .then(d => {
        const k = (d?.keys || []).find((x: any) => x.key === "SHAREPOINT_HOST");
        if (k) setHostStatus({ source: k.source, hasDbValue: k.hasDbValue });
      })
      .catch(() => {});
  };
  useEffect(() => { loadHostStatus(); }, []);

  const saveHost = async () => {
    const value = host.trim();
    if (!value) { setHostMsg({ text: "Enter a host first", ok: false }); return; }
    setHostSaving(true); setHostMsg(null);
    const r = await apiCall("/settings/config-keys/SHAREPOINT_HOST", { method: "PUT", body: JSON.stringify({ value }) });
    setHostSaving(false);
    if (r?.ok) { setHostMsg({ text: "Saved. Effective within 60s.", ok: true }); setHost(""); loadHostStatus(); }
    else setHostMsg({ text: r?.error || "Save failed", ok: false });
  };

  const clearHost = async () => {
    if (!window.confirm("Remove the SHAREPOINT_HOST override? The environment/default fallback re-applies on next restart.")) return;
    setHostSaving(true); setHostMsg(null);
    const r = await apiCall("/settings/config-keys/SHAREPOINT_HOST", { method: "DELETE" });
    setHostSaving(false);
    if (r?.ok) { setHostMsg({ text: "Override removed.", ok: true }); loadHostStatus(); }
    else setHostMsg({ text: r?.error || "Remove failed", ok: false });
  };

  const update = (key: string, value: any) => setSettings((p: any) => ({ ...p, [key]: value }));

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await apiCall("/settings/sharepoint/test", { method: "POST" });
      if (res.success) { setTestResult({ ok: true, message: `Connected! Found ${res.sites.length} site(s)` }); setSites(res.sites); }
      else setTestResult({ ok: false, message: res.error || "Connection failed" });
    } catch (err: any) { setTestResult({ ok: false, message: err.message }); }
    setTesting(false);
  };

  const selectSite = async (siteId: string) => {
    const site = sites.find((s: any) => s.id === siteId);
    update("sharepoint_site_id", siteId);
    update("sharepoint_site_name", site?.name || "");
    setLoadingDrives(true);
    try {
      const res = await apiCall("/settings/sharepoint/drives", { method: "POST", body: JSON.stringify({ site_id: siteId }) });
      setDrives(res.drives || []);
    } catch { setDrives([]); }
    setLoadingDrives(false);
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await apiCall("/settings", { method: "PUT", body: JSON.stringify({ settings }) });
      setMsg("Settings saved successfully");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 20, color: T.txM, fontSize: 13 }}>Loading settings...</div>;

  return (
    <div>
      <SectionTitle sub="Configure Microsoft SharePoint document library sync">SharePoint Integration</SectionTitle>
      {msg && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.grn }}>{msg}</div>}

      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>SharePoint Host</span>
          {hostStatus && (hostStatus.source === "database"
            ? <Badge c={T.grn} b={T.grnS}>Saved (database)</Badge>
            : hostStatus.source === "env"
              ? <Badge c={T.acc} b={T.bg3}>From environment</Badge>
              : <Badge c={T.txD} b={T.bg4}>Not set</Badge>)}
        </div>
        <div style={{ fontSize: 10, color: T.txD, marginBottom: 8 }}>
          The tenant's *.sharepoint.com host used to build document links. Unset → defaults to kattorney.sharepoint.com. Site and library are configured separately below.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            value={host}
            placeholder={hostStatus?.hasDbValue ? "saved — type to replace" : "kattorney.sharepoint.com"}
            onChange={e => setHost(e.target.value)}
            style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
          />
          <Btn v="primary" sz="sm" onClick={() => { saveHost(); }} disabled={hostSaving}>{hostSaving ? "Saving..." : "Save"}</Btn>
          {hostStatus?.hasDbValue && <Btn v="secondary" sz="sm" onClick={() => { clearHost(); }} disabled={hostSaving}>Clear</Btn>}
        </div>
        {hostMsg && <div style={{ marginTop: 8, fontSize: 11, color: hostMsg.ok ? T.grn : T.red }}>{hostMsg.text}</div>}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>SharePoint Sync</span>
          <button onClick={() => update("sharepoint_enabled", settings.sharepoint_enabled === "true" ? "false" : "true")} style={{
            padding: "4px 12px", fontSize: 11, fontWeight: 600, borderRadius: 12, border: "none", cursor: "pointer",
            backgroundColor: settings.sharepoint_enabled === "true" ? T.grn : T.txM,
            color: "#fff"
          }}>{settings.sharepoint_enabled === "true" ? "Enabled" : "Disabled"}</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>1. Test Connection</div>
          <Btn v="secondary" sz="sm" onClick={() => { testConnection(); }} disabled={testing}>{testing ? "Testing..." : "Test SharePoint Connection"}</Btn>
          {testResult && <div style={{ marginTop: 8, padding: "6px 12px", borderRadius: 4, fontSize: 12, backgroundColor: testResult.ok ? T.grnS : T.redS, color: testResult.ok ? T.grn : T.red }}>{testResult.message}</div>}
        </div>

        {(sites.length > 0 || testResult) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>2. Select SharePoint Site</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sites.map((s: any) => (
                <button key={s.id} onClick={() => selectSite(s.id)} style={{
                  padding: "8px 12px", textAlign: "left", border: `1px solid ${settings.sharepoint_site_id === s.id ? T.acc : T.brd}`,
                  borderRadius: 6, cursor: "pointer", fontSize: 12,
                  backgroundColor: settings.sharepoint_site_id === s.id ? T.accS : T.bg3,
                  color: T.tx
                }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>{s.webUrl}</div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 4 }}>Or enter a SharePoint site URL manually:</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input id="spUrlInput" placeholder="https://kattorney.sharepoint.com/sites/KontractAI" style={{ flex: 1, padding: "6px 10px", fontSize: 11, borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit" }} />
                <Btn v="secondary" sz="sm" onClick={async () => {
                  const url = (document.getElementById("spUrlInput") as HTMLInputElement).value;
                  if (!url) return;
                  try {
                    const res = await apiCall("/settings/sharepoint/lookup", { method: "POST", body: JSON.stringify({ site_url: url }) });
                    if (res.success && res.site) {
                      setSites(prev => { const exists = prev.some((s: any) => s.id === res.site.id); return exists ? prev : [...prev, res.site]; });
                      selectSite(res.site.id);
                    }
                  } catch (err: any) { setTestResult({ ok: false, message: "Site not found: " + (err.message || "Check URL") }); }
                }}>Look Up</Btn>
              </div>
            </div>
          </div>
        )}

        {drives.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>3. Select Document Library</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {drives.map((d: any) => (
                <button key={d.id} onClick={() => { update("sharepoint_drive_id", d.id); update("sharepoint_library_name", d.name); }} style={{
                  padding: "8px 12px", textAlign: "left", border: `1px solid ${settings.sharepoint_drive_id === d.id ? T.acc : T.brd}`,
                  borderRadius: 6, cursor: "pointer", fontSize: 12,
                  backgroundColor: settings.sharepoint_drive_id === d.id ? T.accS : T.bg3,
                  color: T.tx
                }}>
                  <div style={{ fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>{d.webUrl}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {loadingDrives && <div style={{ fontSize: 12, color: T.txM }}>Loading document libraries...</div>}

        {settings.sharepoint_site_id && settings.sharepoint_drive_id && (
          <div style={{ padding: "10px 14px", backgroundColor: T.bg3, borderRadius: 6, marginBottom: 16, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: T.tx, marginBottom: 4 }}>Current Configuration</div>
            <div style={{ color: T.txM }}>Site: <span style={{ color: T.tx }}>{settings.sharepoint_site_name || settings.sharepoint_site_id}</span></div>
            <div style={{ color: T.txM }}>Library: <span style={{ color: T.tx }}>{settings.sharepoint_library_name || settings.sharepoint_drive_id}</span></div>
            <div style={{ color: T.txM }}>Status: <span style={{ color: settings.sharepoint_enabled === "true" ? T.grn : T.txM }}>{settings.sharepoint_enabled === "true" ? "Active" : "Inactive"}</span></div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={saving}>{saving ? "Saving..." : "Save Settings"}</Btn>
        </div>
      </Card>
    </div>
  );
};
